/**
 * Enables sharing and synchronizing versioned state objects.
 *
 * ```js
 * // esm
 * import { exchange } from '@paychex/state';
 *
 * // cjs
 * const { exchange } = require('@paychex/state');
 *
 * // iife
 * const { exchange } = window['@paychex/state'];
 *
 * // amd
 * require(['@paychex/state'], function({ exchange }) { ... });
 * define(['@paychex/state'], function({ exchange }) { ... });
 * ```
 *
 * @module exchange
 * @example
 * ```js
 * // creating a state exchange
 * export const market = exchange.create();
 *
 * // add one or more brokers
 * // - memoryStore comes from `@paychex/core`
 * // - storeBroker comes from `@paychex/state-broker-store`
 * market.use(storeBroker(stores.memoryStore()));
 * ```
 * @example
 * ```js
 * // publishing a state instance to the exchange
 *
 * async function converter(from, to) {
 *   if (utils.satisfies(from, to)) // no conversion needed
 *     return [from, JSON.parse(JSON.stringify(snapshot))];
 * }
 * const myUser = await state.create({...}, User);
 * market.publish('user', '1.0.0', myUser, converter);
 * ```
 * @example
 * ```js
 * // consuming a state instance from the exchange
 *
 * async function deserializer(snapshot, version) {
 *   return state.create(snapshot, User);
 * }
 * const userV1 = await market.get('user', '1.x.x', deserializer);
 * ```
 */

import { events, signals } from '@paychex/core';
import { attempt, flatten, isArray, isEqual, noop } from 'lodash';

import { UPDATE as SAVE } from '../models';
import { CONVERT, COMMIT, UPDATE, DISPOSE, GET, SET } from '../events';

import type { Model } from "../models";
import type { EventBus } from '@paychex/core/types/events';
export type { EventBus }

/**
 * A Broker is responsible for pairing {@link Exchange} publishers and consumers. Without brokers,
 * the exchange has no ability to satisfy requests for specific {@link Model} versions. You can
 * create a `@paychex/core` Store-backed broker using the `@paychex/state-broker-store` library,
 * or you can create your own brokers with custom logic.
 *
 * @param bus Your broker will be passed an EventBus shared with the {@link Exchange}.
 * Subscribe and fire events on this bus to communicate with the Exchange and other Brokers.
 * @example
 * ```js
 * // best practice is to wrap your broker in
 * // a factory function that can accept configuration
 * // information, if necessary
 * export function myBrokerFactory(options) {
 *   // the broker accepts an EventBus instance
 *   // and can attach listeners and/or fire its
 *   // own events
 *   return function myBroker(bus) {
 *     bus.on(Events.GET, async (key, range) => { ... });
 *   };
 * }
 * ```
 */
export interface Broker {
    (bus: EventBus): void
}

/**
 * Converts a snapshot at a specific version into a Model instance.
 *
 * **NOTE:** It's good practice to export a deserializer when defining your
 * state schema; that way, consumers don't have to write their own logic.
 *
 * @template T Describes the data structure of the returned {@link Model}.
 * @param snapshot The state snapshot to convert into a {@link Model} instance.
 * @param version The concrete semantic version to convert from. May not be needed.
 * @returns A promise resolved with the new Model instance, or `undefined`.
 * @throws (You can throw an error if you cannot deserialize the specified version.)
 * @example
 * ```js
 * async function deserializer(snapshot, version) {
 *   switch (true) {
 *     case utils.satisfies('1.0.0', version):
 *       return await models.create(snapshot, MySchemaV1);
 *     case utils.satisfies('2.0.0', version):
 *       return await models.create(snapshot, MySchemaV2);
 *     default:
 *       throw errors.error('Unable to deserialize requested version.', { version });
 *   }
 * }
 *
 * const state = await market.get('key', 'v1', deserializer);
 * ```
 */
export interface Deserializer<T = any> {
    (snapshot: Record<string, any>, version: string): Promise<Model<T>>
}

/**
 * Represents the result of a model conversion.
 *
 * @template T The type of snapshot returned by a {@link Converter}.
 * @property The version returned by the converter (e.g. '1.0.0').
 * @property The snapshot returned by the converter.
 */
export type ConversionResult<T> = [string, T?]

/**
 * Converts a snapshot from a specific version to a range.
 *
 * **NOTE:** Make sure your returned snapshot is serialized first. One easy way to do this is to
 * return the results of calling `JSON.parse(JSON.stringify(...))`. See the example for details.
 *
 * **NOTE:** It's good practice to export a converter when defining your
 * state schema; that way, consumers don't have to write their own logic.
 *
 * @template T Describes the snapshot type returned by the converter.
 * @param from The specific version to convert from.
 * @param to The {@link https://docs.npmjs.com/cli/v6/using-npm/semver#ranges semver range} to convert to.
 * @param snapshot The state data snapshot to convert.
 * @returns A promise resolved with an array of 2 elements. The first
 * element is a string giving the exact result version converted to. The second element is the snapshot
 * containing the converted data in the specified version.
 * @throws (You can throw an error if you cannot convert to or from the specified versions.)
 * @example
 * ```js
 * async function converter(from, to, snapshot) {
 *   switch (true) {
 *     case utils.satisfies(from, to):
 *       return [from, JSON.parse(JSON.stringify(snapshot))];
 *     case utils.satisfies(from, 'v1') && utils.satisfies('2.0.0', to):
 *       return ['2.0.0', await upgrade(snapshot)];
 *     case utils.satisfies(from, 'v2') && utils.satisfies('1.0.0', to):
 *       return ['1.0.0', await downgrade(snapshot)];
 *     default:
 *       throw errors.error('Unable to convert snapshot.', { from, to });
 *   }
 * }
 * market.publish('key', '1.0.0', myState, converter);
 * ```
 */
export interface Converter<T = any> {
    (from: string, to: string, snapshot: Record<string, any>): Promise<ConversionResult<T>>
}

/**
 * Coordinates versioned state sharing between publishers and consumers using
 * {@link Broker Brokers}.
 */
export interface Exchange {

    /**
     * Registers one or more {@link Broker Brokers} with the exchange.
     *
     * @param broker The brokers to use with this exchange.
     * @example
     * ```js
     * // creating a state exchange
     * export const market = exchange.create();
     *
     * // add one or more brokers
     * // - memoryStore comes from `@paychex/core`
     * // - storeBroker comes from `@paychex/state-broker-store`
     * market.use(storeBroker(stores.memoryStore()));
     * ```
     */
    use(...broker: Broker[]): void

    /**
     * Retrieves a {@link Model} instance matching the specified key and satisfying the specified {@link https://docs.npmjs.com/cli/v6/using-npm/semver#ranges semver range}.
     *
     * @template T The type of Model instance to return.
     * @param key The unique identifier of the {@link Model} instance to retrieve. Should match whatever identifier was used by the publisher.
     * @param version The {@link https://docs.npmjs.com/cli/v6/using-npm/semver#ranges semver range}. The returned {@link Model} instance will match this range.
     * @param deserializer Rehydrates a {@link Model} instance from a snapshot.
     * @returns A Promise resolved with an instance of the requested Model version.
     * @example
     * ```js
     * async function deserializer(snapshot, version) {
     *   return state.create(snapshot, User);
     * }
     * const userV1 = await market.get('user', '1.x.x', deserializer);
     * ```
     */
    get<T = any>(key: string, version: string, deserializer: Deserializer<T>): Promise<Model<T>>

    /**
     * Shares the specific {@link Model} version instance with any consumers.
     *
     * @template T The type of Model instance to publish.
     * @param key The unique identifier of the {@link Model} instance to publish. Consumers will need to specify this same key when requesting a copy.
     * @param version The exact semantic version of the {@link Model} to publish.
     * @param state The Model instance to share with consumers.
     * @param converter A function that can be used to convert between the published version and requested versions.
     * @example
     * ```js
     * async function converter(from, to) {
     *   if (utils.satisfies(from, to)) // no conversion needed
     *     return [from, JSON.parse(JSON.stringify(snapshot))];
     * }
     * const myUser = await state.create({...}, User);
     * market.publish('user', '1.0.0', myUser, converter);
     * ```
     */
    publish<T = any>(key: string, version: string, state: Model<T>, converter: Converter<T>): void

}

const COMPLETED = Symbol('completed');

function withSetCompletion(bus: EventBus): EventBus {

    let count = 0;

    function decrement() {
        if (--count === 0)
            bus.fire(COMPLETED);
        count = Math.max(0, count);
    }

    function ensureArray<T = any>(result: T|T[]): T[] {
        return isArray(result) ? result : [];
    }

    async function fire(key: string|symbol, ...args: any[]): Promise<any[]> {
        const promise = bus.fire(key, ...args)
            .then(ensureArray);
        if (key !== SET)
            return promise;
        count++;
        return promise.finally(decrement);
    }

    return {
        ...bus,
        fire,
    };

}

/**
 * Creates an exchange instance you can configure with {@link Broker Brokers}
 * to enable publishing and consuming versioned {@link Model} instances.
 *
 * @returns An empty exchange instance.
 * @example
 * ```js
 * export const market = exchange.create();
 * ```
 */
export function create(): Exchange {

    /* eslint "prefer-rest-params": 0 */ // unit test only
    const busFactory = arguments[0] || events.bus;

    const bus = withSetCompletion(busFactory());
    const instantiate = (factory: CallableFunction) => factory(bus);

    function use(...brokerFactories: CallableFunction[]) {
        flatten(brokerFactories).forEach(instantiate);
    }

    async function waitForResult(key: string, pattern: string) {
        for(;;) {
            const results = await bus.fire(GET, key, pattern);
            const result = results.find(Boolean);
            if (result)
                return result;
            const signal = signals.autoReset(false);
            bus.one(COMPLETED, signal.set);
            await signal.ready();
        }
    }

    async function get<T = any>(key: string, pattern: string, deserializer: Deserializer<T>): Promise<Model<T>> {

        const handlers: VoidFunction[] = [];
        const [version, data] = await waitForResult(key, pattern);

        let state = await deserializer(data, version);

        handlers.push(

            bus.on(UPDATE, function synchronize(k: string, v: string, snapshot: Record<string, any>) {
                if (k == key && v == version && !isEqual(snapshot, state.snapshot()))
                    return state.fire(SAVE, snapshot);
            }),

            state.on(COMMIT, function updateResolvers(snapshot: Record<string, any>) {
                Promise.all([
                    bus.fire(SET, key, version, snapshot),
                    bus.fire(UPDATE, key, version, snapshot),
                ]).catch(noop);
            }),

            state.on(DISPOSE, function cleanUp() {
                state = deserializer = null;
                handlers.forEach(attempt);
                handlers.length = 0;
            }),

        );

        return state;

    }

    function publish<T = any>(key: string, version: string, state: Model<T>, converter: Converter<T>) {

        const handlers = [

            state.on(COMMIT, function announceChange(snapshot: T) {
                bus.fire(SET, key, version, snapshot);
            }),

            bus.on(CONVERT, function convert(k: string, from: string, to: string, snapshot: T) {
                if (k === key)
                    return converter(from, to, snapshot).catch(noop);
            }),

            bus.on(UPDATE, async function synchronize(k: string, v: string, snapshot: T) {
                if (k == key && !isEqual(snapshot, state.snapshot())) {
                    const result = await converter(v, version, snapshot);
                    if (result)
                        return state.fire(SAVE, result[1]);
                }
            }),

            state.on(DISPOSE, function cleanUp() {
                converter = state = null;
                handlers.forEach(attempt);
                handlers.length = 0;
            }),

        ];

        bus.fire(SET, key, version, state.snapshot());

    }

    return {
        use,
        get,
        publish,
    };

}